// The things lab (`?scene=things`, gate C5) as a variant of the valley's machinery: the Wanderer on its controller, the
// camera and the look, on the lab's ground (lab.ts), with the joined fight (src/enemies/fight.ts: combat, so blows land
// on breakables; the crowd, so the walker can show the navmesh's pass opening; the bodies module, for the gather pose)
// and the things (module.ts). No encounter: the fight has the hero, the breakables as targets and the walker.
import type { Runtime } from "@rpg/runtime";
import type { SpikeContent } from "../content";
import { FIGHT_LAYERS, FIGHT_SOLVER_ITERATIONS, createFight, readFightContent, type Fight } from "../enemies/fight";
import { VALLEY_LAYERS } from "../valley/course";
import type { ValleyVariant } from "../valley/scene";
import { readThingsContent, type ThingsContent } from "./content";
import { LAB_MOUNT, LAB_SPAWN, WALKER, labCourse, labHeight, labNav } from "./lab";
import { BREAKABLE_IDS, THINGS_LAYERS, createThings, dynamicNavShapes, type Things } from "./module";

/** The lab's parts for the sim mount. */
export interface ThingsLabMount {
  variant: ValleyVariant;
  content: ThingsContent;
  things(): Things;
  fight(): Fight;
}

/** The things lab. */
export function thingsLab(o: { content: SpikeContent; runtime: Runtime }): ThingsLabMount {
  const fightContent = readFightContent(o.content);
  const content = readThingsContent(o.content);
  let fight: Fight | null = null;
  let things: Things | null = null;
  const variant: ValleyVariant = {
    name: "things",
    layers: { ...VALLEY_LAYERS, ...FIGHT_LAYERS, ...THINGS_LAYERS },
    course: () => labCourse(),
    height: labHeight,
    spawn: LAB_SPAWN,
    mountSpawn: LAB_MOUNT,
    solverIterations: FIGHT_SOLVER_ITERATIONS,
    extend({ rig, rng, actions }) {
      const nav = labNav(rig.course, dynamicNavShapes(content, labHeight));
      const f = createFight({
        collision: rig.collision,
        actions,
        rng,
        nav,
        content: fightContent,
        slowMo: (scale, s, ease) => o.runtime.clock.slowMo(scale, s, ease),
        hero: () => rig.hero(),
        heroCtl: () => rig.heroCtl,
        cameraYaw: () => rig.control().cameraYaw,
        respawnAt: () => LAB_SPAWN,
        terrainAt: (x, z) => rig.terrainAt(x, z),
        // the breakables are combat targets (team "prop"): they stand where they were placed
        otherPose: (id) => {
          if (!things || id < BREAKABLE_IDS) return null;
          const p = things.content.placements.filter((x) => x.kind === "breakable")[id - BREAKABLE_IDS];
          return p ? { feet: things.at(p.id), yaw: p.yaw } : null;
        },
        encounter: null,
        director: false,
        difficulty: "normal",
      });
      const t = createThings({ collision: rig.collision, actions, rng, content, fight: () => f, nav, hero: () => rig.hero(), groundAt: labHeight });
      fight = f;
      things = t;
      return {
        modules: [...f.modules, ...t.modules],
        build: (w) => {
          f.build(w);
          // the walker: a crowd agent heading through the pass (held at the crate until the pass opens)
          const from: [number, number, number] = [WALKER.from[0], labHeight(...WALKER.from), WALKER.from[1]];
          f.crowd.add(WALKER.id, from, { radius: 0.35, height: 1.6, maxSpeed: 2.2, maxAccel: 10, separation: 0 });
          f.crowd.moveTo(WALKER.id, [WALKER.to[0], labHeight(...WALKER.to), WALKER.to[1]], 0.4);
          t.build(w);
        },
        hashed: [...f.hashed, ...t.hashed],
        heroDrive: () => t.heroDrive(),
        claimsInteract: () => t.claimsInteract(),
      };
    },
  };
  return {
    variant,
    content,
    things: () => {
      if (!things) throw new Error("things lab: not set up yet");
      return things;
    },
    fight: () => {
      if (!fight) throw new Error("things lab: not set up yet");
      return fight;
    },
  };
}
